import { useContext, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { Paper, Box } from "@mui/material"
import { chain } from "lodash"
import Error from "components/error"
import Loading from "components/loading"
import { ERROR, LOADING } from "helpers/data-statuses"
import { getDriftStartAndEndDates } from "helpers/dates"
import { CONTENT_SET_METADATA } from "resources/content-sets"
import { useModelMetadata } from "resources/models"
import { useInstrumentsWithQueryOptions, getOrderedInstrumentColumns, useInstrumentsEarliestAndLatestDates } from "connected-components/dashboard/helpers"
import FactorsViewSelector from "connected-components/dashboard/components/view-selectors/factors"
import useFactorsViewContext from "connected-components/dashboard/context/factors-view"
import { useAnalytics } from "connected-components/analytics"
import { SHARED_EVENTS } from "connected-components/analytics/shared/events"
import { FactorsViewAnalyticsProvider, useFactorsViewLoadedTracking, FACTORS_VIEW_EVENTS } from "./analytics"
import riskTemplate from "./templates/risk"
import performanceTemplate from "./templates/performance"
import contentSetTemplate from "./templates/content-set"
import betaTemplate from "./templates/beta"
import { DRIFT_DATES_SHAPE } from "./prop-types"
import "./style.scss"

export default function FactorsViewWrapper(props) {
  const { context: parentContext } = props
  const [FactorsViewContext, contextValue] = useFactorsViewContext(parentContext)

  return <FactorsViewContext.Provider value={contextValue}>
    <FactorsViewAnalyticsProvider>
      <FactorsView
        {...{
            ...props,
            context: FactorsViewContext,
          }}
      />
    </FactorsViewAnalyticsProvider>
  </FactorsViewContext.Provider>
}

FactorsViewWrapper.propTypes = {
  context: PropTypes.object.isRequired,
}

function makeViewTemplates(contentSets) {
  const contentSetTemplates = chain(contentSets)
    .keyBy("id")
    .mapValues(() => contentSetTemplate)
    .value()

  return {
    risk: riskTemplate,
    performance: performanceTemplate,
    beta: betaTemplate,
    ...contentSetTemplates,
  }
}

function FactorsView(props) {
  const { context, instrumentsWithMetadata, contentSets } = props
  const { modelId, endDate, view, customViewMetricsStatus, selectedMode, instrument: original, comparisonInstrument: comparison, secondInstrument: rebalanced, filter, normalization, isExpanded } = useContext(context)
  const [modelMetadata] = useModelMetadata(modelId)

  const instrumentOptions = { filter, normalization, isExpanded }
  const instrumentsWithQueryOptions = useInstrumentsWithQueryOptions({ original, rebalanced, comparison }, selectedMode, instrumentOptions)
  const instrumentColumns = useMemo(() => getOrderedInstrumentColumns(selectedMode), [selectedMode])
  const viewTemplates = useMemo(() => makeViewTemplates(contentSets), [contentSets])
  const [boundaryEarliestDate] = useInstrumentsEarliestAndLatestDates(modelMetadata, instrumentsWithMetadata)
  const driftDates = getDriftStartAndEndDates(endDate, boundaryEarliestDate)
  const selectedContentSetMetadata = useMemo(() => contentSets.find(contentSet => contentSet.id === view), [contentSets, view])

  const analytics = useAnalytics()
  useFactorsViewLoadedTracking()

  const handleViewSelected = useCallback((viewName) => {
    analytics.track(FACTORS_VIEW_EVENTS.VIEW_SELECTED, { viewName })
  }, [analytics])

  if (customViewMetricsStatus === LOADING || !endDate || !modelId) {
    return <Loading />
  }
  if (customViewMetricsStatus === ERROR) {
    analytics.track(SHARED_EVENTS.ERROR, {
      errorType: "data_loading_error",
      errorMessage: "Failed to load factors view data",
    })
    return <Error />
  }

  const template = viewTemplates[view] || viewTemplates.performance

  // TODO: [APP-1566] We probably want to adjust the start date to something that's meaningful for calculating drifts
  const content = <Box className="factors-view">
    <Paper square style={{ display: "flex", alignItems: "center", minHeight: 54, paddingLeft: "1rem", paddingRight: "1rem" }}>
      <FactorsViewSelector
        context={context}
        contentSets={contentSets}
        onViewSelected={handleViewSelected}
      />
      <div style={{ minWidth: "3rem" }} />
      {template.Header && <template.Header context={context} contentSets={contentSets} view={view} driftDates={driftDates} />}
    </Paper>
    <template.Component
      instrumentsWithMetadata={instrumentsWithMetadata}
      instrumentsWithQueryOptions={instrumentsWithQueryOptions}
      instrumentColumns={instrumentColumns}
      selectedContentSetMetadata={selectedContentSetMetadata}
      view={view}
      driftDates={driftDates}
      {...props}
    />
  </Box>

  if (template.Context) {
    return <template.Context>
      {content}
    </template.Context>
  }

  return content
}

FactorsView.propTypes = {
  context: PropTypes.object.isRequired,
  instrumentsWithMetadata: PropTypes.shape({
    original: PropTypes.object,
    rebalanced: PropTypes.object,
    comparison: PropTypes.object,
    base: PropTypes.object,
  }).isRequired,
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
  driftDates: DRIFT_DATES_SHAPE.isRequired,
}
